import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'

interface MapLayoutProps {
  children: React.ReactNode
  showInfrastructure: boolean
  onToggleInfrastructure: () => void
  showDzialki: boolean
  onToggleDzialki: () => void
  showBudynki: boolean
  onToggleBudynki: () => void
  showRejestrWlasnosci?: boolean
  onToggleRejestrWlasnosci?: () => void
  showGaz?: boolean
  onToggleGaz?: () => void
  showTeletechnika?: boolean
  onToggleTeletechnika?: () => void
  showElektryka?: boolean
  onToggleElektryka?: () => void
  featureInfoMode?: boolean
  onToggleFeatureInfo?: () => void
  // Podkład: ortofotomapa (domyślnie wyłączona)
  showOrtofoto?: boolean
  onToggleOrtofoto?: () => void
  showFlows?: boolean
  onToggleFlows?: () => void
  onManageControllers?: () => void
  showWodociagi: boolean
  onToggleWodociagi: () => void
  showKanalizacja: boolean
  onToggleKanalizacja: () => void
  showGraniceMiejscowosci?: boolean
  onToggleGraniceMiejscowosci?: () => void
  // Tryb edycji
  editMode?: boolean
  onToggleEditMode?: () => void
  // Odcinki wodociągów
  showWaterSegments?: boolean
  onToggleWaterSegments?: () => void
  showUserLocation?: boolean
  onToggleUserLocation?: () => void
  showMeasurementPoints?: boolean
  onToggleMeasurementPoints?: () => void
  // Sewer segments
  showSewerSegments?: boolean
  onToggleSewerSegments?: () => void
  // Realizations layer
  showRealizations?: boolean
  onToggleRealizations?: () => void
  // Network valves (zasuwy sieciowe)
  showNetworkValves?: boolean
  onToggleNetworkValves?: () => void
  onStartPlacingValve?: () => void
  // Network hydrants (hydranty)
  showNetworkHydrants?: boolean
  onToggleNetworkHydrants?: () => void
  onStartPlacingHydrant?: () => void
  onImportHydrants?: () => void
  // Funkcje rysowania linii
  onStartDrawingSegment?: () => void
  onStartDrawingSewerSegment?: () => void
  onStartEditingSegment?: () => void
  onStartCreatingPoint?: () => void
  showDrawingControls?: boolean
  onRemoveLastPoint?: () => void
  onCancelDrawing?: () => void
  onConfirmSegment?: () => void
  // Segment type selection
  segmentType?: 'water' | 'sewer'
  onSegmentTypeChange?: (type: 'water' | 'sewer') => void
}

enum ViewMode {
  VIEWING = 'viewing',
  EDITING = 'editing'
}

// Nowoczesny wiersz warstwy: duże pole dotykowe + przełącznik (switch)
function LayerToggle({
  checked,
  onChange,
  icon,
  label
}: {
  checked: boolean
  onChange: () => void
  icon: string
  label: string
}) {
  return (
    <label className="flex items-center justify-between gap-3 px-3 min-h-[44px] rounded-xl hover:bg-gray-50 active:bg-gray-100 cursor-pointer transition-colors select-none">
      <span className="flex items-center gap-2.5 text-sm text-gray-700 min-w-0">
        <span className="text-base">{icon}</span>
        <span className="truncate">{label}</span>
      </span>
      <span className="relative inline-flex shrink-0 items-center">
        <input type="checkbox" checked={checked} onChange={onChange} className="peer sr-only" />
        <span className="w-10 h-6 rounded-full bg-gray-200 peer-checked:bg-blue-600 transition-colors duration-200"></span>
        <span className="absolute left-0.5 top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 peer-checked:translate-x-4"></span>
      </span>
    </label>
  )
}

// Przycisk narzędzia edycji: duże pole dotykowe, wyraźny stan aktywny
function ToolButton({
  onClick,
  icon,
  label
}: {
  onClick: () => void
  icon: string
  label: string
}) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 min-h-[48px] text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 hover:border-gray-300 active:bg-gray-100 rounded-xl shadow-sm transition-colors"
    >
      <span className="text-lg">{icon}</span>
      <span className="font-medium text-sm text-left">{label}</span>
    </button>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest px-1 mb-2">
      {children}
    </h3>
  )
}

export default function MapLayout({
  children,
  showInfrastructure,
  onToggleInfrastructure,
  showDzialki,
  onToggleDzialki,
  showBudynki,
  onToggleBudynki,
  showRejestrWlasnosci = false,
  onToggleRejestrWlasnosci = () => {},
  showGaz = false,
  onToggleGaz = () => {},
  showTeletechnika = false,
  onToggleTeletechnika = () => {},
  showElektryka = false,
  onToggleElektryka = () => {},
  featureInfoMode = false,
  onToggleFeatureInfo = () => {},
  showFlows = false,
  onToggleFlows = () => {},
  onManageControllers = () => {},
  showWodociagi,
  onToggleWodociagi,
  showKanalizacja,
  onToggleKanalizacja,
  showGraniceMiejscowosci = false,
  onToggleGraniceMiejscowosci = () => {},
  editMode = false,
  onToggleEditMode = () => {},
  showWaterSegments = false,
  onToggleWaterSegments = () => {},
  showSewerSegments = false,
  onToggleSewerSegments = () => {},
  showUserLocation = false,
  onToggleUserLocation = () => {},
  showMeasurementPoints = false,
  onToggleMeasurementPoints = () => {},
  showRealizations = false,
  onToggleRealizations = () => {},
  showNetworkValves = false,
  onToggleNetworkValves = () => {},
  onStartPlacingValve = () => {},
  showNetworkHydrants = false,
  onToggleNetworkHydrants = () => {},
  onStartPlacingHydrant = () => {},
  onImportHydrants = () => {},
  showOrtofoto = false,
  onToggleOrtofoto = () => {},
  onStartDrawingSegment = () => {},
  onStartEditingSegment = () => {},
  onStartCreatingPoint = () => {},
  showDrawingControls = false,
  onRemoveLastPoint = () => {},
  onCancelDrawing = () => {},
  onConfirmSegment = () => {}
}: MapLayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.VIEWING)
  const navigate = useNavigate()

  const handleBackToDashboard = () => {
    navigate('/dashboard')
  }

  // Narzędzia wymagające kliknięcia na mapie - na mobile/tablecie zamykamy sidebar,
  // aby nie zasłaniał mapy.
  const runAndCloseSidebar = (fn?: () => void) => () => {
    setSidebarOpen(false)
    fn?.()
  }

  const setViewModeAndUpdateEditMode = (mode: ViewMode) => {
    setViewMode(mode)
    if (mode === ViewMode.EDITING) {
      onToggleEditMode()
    } else {
      // Jeśli zmieniamy na przeglądanie, upewnij się że editMode jest false
      if (editMode) {
        onToggleEditMode()
      }
    }
  }

  return (
    <div className="h-screen overflow-hidden bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-white shadow-2xl transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} transition-transform duration-300 ease-in-out xl:translate-x-0 xl:static xl:inset-0 xl:w-64 xl:shadow-lg overflow-y-auto overscroll-contain`}>
        <div className="flex flex-col h-full">
          {/* Logo + zamknięcie (mobile) */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-100">
            <img
              src="/mnexus_logo 500 x 200.png"
              alt="MNexus Logo"
              className="h-11 w-auto"
            />
            <button
              onClick={() => setSidebarOpen(false)}
              className="xl:hidden p-2.5 -mr-1 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 active:bg-gray-200 transition-colors"
              aria-label="Zamknij panel"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Back to Dashboard */}
          <div className="px-3 pt-3">
            <button
              onClick={handleBackToDashboard}
              className="w-full flex items-center gap-3 px-4 min-h-[44px] text-gray-600 hover:bg-gray-50 active:bg-gray-100 hover:text-gray-900 rounded-xl transition-colors"
            >
              <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              <span className="font-medium text-sm">Wróć do Dashboard</span>
            </button>
          </div>

          {/* Tabs: Przeglądanie/Edycja */}
          <div className="px-3 pt-3 pb-1">
            <div className="flex bg-gray-100 rounded-xl p-1">
              <button
                onClick={() => setViewModeAndUpdateEditMode(ViewMode.VIEWING)}
                className={`flex-1 min-h-[40px] px-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                  viewMode === ViewMode.VIEWING
                    ? 'bg-white text-blue-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 active:text-gray-900'
                }`}
              >
                Przeglądanie
              </button>
              <button
                onClick={() => setViewModeAndUpdateEditMode(ViewMode.EDITING)}
                className={`flex-1 min-h-[40px] px-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                  viewMode === ViewMode.EDITING
                    ? 'bg-white text-blue-700 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 active:text-gray-900'
                }`}
              >
                Edycja
              </button>
            </div>
          </div>

          {/* Opcje mapy */}
          <nav className="flex-1 px-3 py-3 space-y-5 safe-bottom">
            {viewMode === ViewMode.VIEWING && (
              <>
                {/* Narzędzia */}
                <div>
                  <SectionLabel>Narzędzia</SectionLabel>
                  <label className={`flex items-center justify-between gap-3 px-3 min-h-[44px] rounded-xl cursor-pointer transition-colors select-none ${featureInfoMode ? 'bg-indigo-50' : 'hover:bg-gray-50 active:bg-gray-100'}`}>
                    <span className="flex items-center gap-2.5 text-sm min-w-0">
                      <span className="text-base">ℹ️</span>
                      <span className={`truncate ${featureInfoMode ? 'text-indigo-700 font-medium' : 'text-gray-700'}`}>Informacje o obiekcie</span>
                    </span>
                    <span className="relative inline-flex shrink-0 items-center">
                      <input type="checkbox" checked={featureInfoMode} onChange={onToggleFeatureInfo} className="peer sr-only" />
                      <span className="w-10 h-6 rounded-full bg-gray-200 peer-checked:bg-indigo-600 transition-colors duration-200"></span>
                      <span className="absolute left-0.5 top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-200 peer-checked:translate-x-4"></span>
                    </span>
                  </label>
                  <p className="px-3 mt-1 text-[11px] leading-snug text-gray-400">
                    Po włączeniu kliknięcie obiektu sieci (np. wodociągu) pokaże jego dane z ewidencji powiatu.
                  </p>
                </div>

                {/* Podkład mapy */}
                <div>
                  <SectionLabel>Podkład mapy</SectionLabel>
                  <div className="space-y-0.5">
                    <LayerToggle checked={showOrtofoto} onChange={onToggleOrtofoto} icon="🛰️" label="Ortofotomapa" />
                  </div>
                </div>

                {/* Dane powiatowe (WMS) */}
                <div>
                  <SectionLabel>Dane powiatowe</SectionLabel>
                  <div className="space-y-0.5">
                    <LayerToggle checked={showDzialki} onChange={onToggleDzialki} icon="📐" label="Działki" />
                    <LayerToggle checked={showBudynki} onChange={onToggleBudynki} icon="🏢" label="Budynki" />
                    <LayerToggle checked={showRejestrWlasnosci} onChange={onToggleRejestrWlasnosci} icon="📜" label="Rejestr własności" />
                    <LayerToggle checked={showWodociagi} onChange={onToggleWodociagi} icon="💧" label="Wodociągi" />
                    <LayerToggle checked={showKanalizacja} onChange={onToggleKanalizacja} icon="🚽" label="Kanalizacja" />
                    <LayerToggle checked={showGaz} onChange={onToggleGaz} icon="🔥" label="Gazociągi" />
                    <LayerToggle checked={showTeletechnika} onChange={onToggleTeletechnika} icon="📡" label="Teletechnika" />
                    <LayerToggle checked={showElektryka} onChange={onToggleElektryka} icon="⚡" label="Elektroenergetyka" />
                    <LayerToggle checked={showGraniceMiejscowosci} onChange={onToggleGraniceMiejscowosci} icon="🏘️" label="Granice miejscowości" />
                  </div>
                </div>

                {/* Warstwy lokalne */}
                <div>
                  <SectionLabel>Warstwy lokalne</SectionLabel>
                  <div className="space-y-0.5">
                    <LayerToggle checked={showInfrastructure} onChange={onToggleInfrastructure} icon="📍" label="Infrastruktura" />
                    <LayerToggle checked={showWaterSegments} onChange={onToggleWaterSegments} icon="🏗️" label="Wodociągi" />
                    <LayerToggle checked={showSewerSegments} onChange={onToggleSewerSegments} icon="🚽" label="Kanalizacja" />
                    <LayerToggle checked={showMeasurementPoints} onChange={onToggleMeasurementPoints} icon="📊" label="Punkty pomiarowe" />
                    <LayerToggle checked={showFlows} onChange={onToggleFlows} icon="💠" label="Przepływy (live)" />
                    <LayerToggle checked={showRealizations} onChange={onToggleRealizations} icon="🏗️" label="Realizacje" />
                    <LayerToggle checked={showNetworkValves} onChange={onToggleNetworkValves} icon="🔴" label="Zasuwy sieciowe" />
                    <LayerToggle checked={showNetworkHydrants} onChange={onToggleNetworkHydrants} icon="🚒" label="Hydranty" />
                    {showNetworkHydrants && (
                      <div className="ml-7 mt-1 mb-1 flex flex-col gap-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-green-600" /> Kontrola pozytywna</span>
                        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-600" /> Kontrola negatywna</span>
                        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-gray-400" /> Niesprawdzony</span>
                        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-gray-300 ring-2 ring-blue-600" /> Punkt płukania (obwódka)</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Moja lokalizacja */}
                <div>
                  <SectionLabel>Lokalizacja</SectionLabel>
                  <LayerToggle checked={showUserLocation} onChange={onToggleUserLocation} icon="📱" label="Moja lokalizacja (GPS)" />
                </div>
              </>
            )}

            {viewMode === ViewMode.EDITING && (
              <div>
                <SectionLabel>Narzędzia edycji</SectionLabel>
                <div className="space-y-2">
                  <ToolButton onClick={runAndCloseSidebar(onStartCreatingPoint)} icon="📊" label="Dodaj punkt pomiarowy" />
                  <ToolButton onClick={onManageControllers} icon="📟" label="Kontrolery telemetryczne" />
                  <ToolButton onClick={runAndCloseSidebar(onStartEditingSegment)} icon="✏️" label="Edycja odcinka" />
                  <ToolButton onClick={runAndCloseSidebar(onStartDrawingSegment)} icon="📏" label="Rysuj odcinek" />
                  <ToolButton onClick={runAndCloseSidebar(onStartPlacingValve)} icon="🔴" label="Dodaj zasuwę sieciową" />
                  <ToolButton onClick={runAndCloseSidebar(onStartPlacingHydrant)} icon="🚒" label="Dodaj hydrant" />
                  <ToolButton onClick={runAndCloseSidebar(onImportHydrants)} icon="📥" label="Importuj hydranty (XLSX)" />
                </div>

                {/* Przyciski kontrolne rysowania - w sidebarze (desktop); na mobile
                    dostępne są też na pływającym pasku na mapie */}
                {showDrawingControls && (
                  <div className="mt-3 space-y-1.5 bg-gray-50 border border-gray-100 rounded-xl p-2">
                    <button
                      onClick={onRemoveLastPoint}
                      className="w-full flex items-center gap-2 px-3 min-h-[40px] text-orange-600 hover:bg-orange-50 active:bg-orange-100 rounded-lg transition-colors text-sm"
                    >
                      <span className="text-base">🔄</span>
                      <span>Usuń ostatni punkt</span>
                    </button>
                    <button
                      onClick={onCancelDrawing}
                      className="w-full flex items-center gap-2 px-3 min-h-[40px] text-red-600 hover:bg-red-50 active:bg-red-100 rounded-lg transition-colors text-sm"
                    >
                      <span className="text-base">❌</span>
                      <span>Anuluj</span>
                    </button>
                    <button
                      onClick={onConfirmSegment}
                      className="w-full flex items-center justify-center gap-2 px-3 min-h-[44px] bg-green-600 text-white hover:bg-green-700 active:bg-green-800 rounded-lg transition-colors text-sm font-medium"
                    >
                      <span className="text-base">✅</span>
                      <span>Zatwierdź odcinek</span>
                    </button>
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* Footer */}
          <div className="border-t border-gray-100 px-4 py-3 safe-bottom">
            <div className="text-xs text-gray-400 text-center">
              Mapa Infrastruktury MNexus
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 min-h-0">
        {/* Top bar */}
        <header className="bg-white/90 backdrop-blur shadow-sm border-b border-gray-100 sticky top-0 z-40">
          <div className="px-3 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-14 sm:h-16">
              {/* Hamburger menu for mobile/tablet */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="xl:hidden flex items-center gap-2 px-3 min-h-[44px] rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100 active:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                <span className="text-sm font-medium">Warstwy</span>
              </button>

              {/* Title */}
              <div className="flex-1 xl:flex-none text-center xl:text-left">
                <h1 className="text-base sm:text-xl font-semibold text-gray-900">🗺️ Mapa Infrastruktury</h1>
              </div>

              {/* Empty space for future controls */}
              <div className="w-10 xl:w-auto"></div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 min-h-0 overflow-hidden">
          {children}
        </main>
      </div>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] xl:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  )
}
